/**
 * Wires the overdue-invoice feed to its two surfaces: the always-on warning bar
 * and the review-and-collect dialog behind it.
 *
 * Mounted ONCE, by `crm-layout`, so the warning is present on every CRM page —
 * the layout stays a one-liner and every hook in the feature lives here (never
 * conditionally, per the repo's `rules-of-hooks` gate).
 *
 * Access is decided inside `useOverdueInvoices`: a viewer without both
 * `invoices:access` and `financials:access` never triggers a request, and the
 * summary stays at zero, so the banner renders nothing.
 */
import { useCallback, useState } from "react"

import { OverdueInvoicesBanner } from "@crm-src/components/overdue-invoices-banner"
import { OverdueInvoicesDialog } from "@crm-src/components/overdue-invoices-dialog"
import { useOverdueInvoices } from "@crm-src/hooks/useOverdueInvoices"

export function OverdueInvoicesAlert() {
  const { invoices, summary, severity, loading, markPaid, markUnpaid } = useOverdueInvoices()
  const [open, setOpen] = useState(false)

  const handleOpen = useCallback(() => setOpen(true), [])
  const handleClose = useCallback(() => setOpen(false), [])

  return (
    <>
      <OverdueInvoicesBanner summary={summary} severity={severity} onOpen={handleOpen} />
      {/* Mounted only while open — the dialog is dead weight on every CRM page
          otherwise, and a fresh mount resets its per-session "settled" rows. */}
      {open && (
        <OverdueInvoicesDialog
          open={open}
          onClose={handleClose}
          invoices={invoices}
          summary={summary}
          loading={loading}
          onMarkPaid={markPaid}
          onMarkUnpaid={markUnpaid}
        />
      )}
    </>
  )
}

export default OverdueInvoicesAlert
